import React from 'react';
import { View, useWindowDimensions } from 'react-native';
import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';

// Define the IconSvgElement type
type IconSvgElement = readonly (readonly [
  string,
  {
    readonly [key: string]: string | number;
  },
])[];

interface MessageViewProps {
  type: 'error' | 'warning' | 'success' | 'information' | 'info';
  text: string;
  subText?: string;
  icon?: IconSvgElement | undefined;
  iconSize?: number;
  textClassName?: string;
  children?: React.ReactNode;
  disableHeight?: boolean;
  /**
   * Use inside FlashList/FlatList ListEmptyComponent to center content vertically.
   * Pass `true` to use 60% of window height, or a number to set an explicit minHeight in pixels.
   */
  listEmpty?: boolean | number;
}

export function MessageView({
  type,
  text,
  subText,
  icon,
  iconSize = 72,
  textClassName,
  children,
  disableHeight = false,
  listEmpty = false,
}: MessageViewProps) {
  const { height: windowHeight } = useWindowDimensions();

  const getColorClass = () => {
    switch (type) {
      case 'error':
        return 'color-error-400';
      case 'warning':
        return 'color-warning-400';
      case 'success':
        return 'color-success-400';
      default:
        return 'color-typography-400';
    }
  };

  const listEmptyMinHeight = listEmpty
    ? typeof listEmpty === 'number'
      ? listEmpty
      : windowHeight * 0.6
    : undefined;

  const containerClassName = `${disableHeight || listEmpty ? '' : 'h-[70%]'} w-full items-center justify-center gap-4 p-4`;

  return (
    <View
      className={containerClassName}
      style={listEmptyMinHeight !== undefined ? { minHeight: listEmptyMinHeight } : undefined}>
      {icon && <HugeIconsWrapper icon={icon} size={iconSize} className={getColorClass()} />}
      <Text
        size="lg"
        bold
        className={`w-full self-stretch min-w-0 text-center text-typography-700 ${textClassName ?? ''}`}>
        {text}
      </Text>
      {subText ? (
        <Text size="sm" className="w-full self-stretch min-w-0 text-center text-typography-500">
          {subText}
        </Text>
      ) : null}
      {children}
    </View>
  );
}
